import { cn } from '@/lib/utils';
import { Button } from '@pms-digital/design-system';
import { type ChangeEvent, useId, useRef } from 'react';
import { ACCEPT_FICHIER } from './lire-fichier';
import { useApresDelai } from './use-apres-delai';

interface LigneTitreProps {
  titre: string;
  onTitreChange: (titre: string) => void;
  /** The title proposed while the field is empty ("" when there is none); it is the one sent. */
  titrePropose: string;
  /** Locked while a translation is running. */
  verrouille: boolean;
  /** A file is being read: "charger un fichier" is locked once the read has lasted 300 ms. */
  lecture: boolean;
  /** A file was picked with "charger un fichier". */
  onFichierChoisi: (fichier: File) => void;
}

/**
 * The line above the text zone: the optional title (a bare field with a single net
 * under it) on the left, "charger un fichier" on the right. Empty, the field shows the
 * proposed title in grey in place of its own invitation (S12): it is what is sent if
 * the user types nothing. Both placeholders are `gray-600` (5.4:1 on white): the lighter grey
 * of the board, `gray-400`, is 2.9:1 and does not pass AA (S14, #5).
 */
export function LigneTitre({
  titre,
  onTitreChange,
  titrePropose,
  verrouille,
  lecture,
  onFichierChoisi,
}: LigneTitreProps) {
  const refEntree = useRef<HTMLInputElement>(null);
  const idPropose = useId();
  const lectureAffichee = useApresDelai(lecture);
  const propose = titrePropose !== '';

  function surChoix(evenement: ChangeEvent<HTMLInputElement>) {
    const fichier = evenement.target.files?.[0];
    // Emptied so that picking the same file again still fires a change.
    evenement.target.value = '';
    if (fichier !== undefined) onFichierChoisi(fichier);
  }

  return (
    <div className="mt-8 flex items-center gap-6">
      <input
        type="text"
        aria-label="titre du texte, facultatif"
        aria-describedby={propose && titre === '' ? idPropose : undefined}
        placeholder={propose ? titrePropose : 'Ajouter un titre (facultatif)'}
        value={titre}
        disabled={verrouille}
        onChange={(evenement) => onTitreChange(evenement.target.value)}
        className={cn(
          'h-11 min-w-0 flex-1 border-0 border-gray-500 border-b bg-transparent px-1 font-semibold text-black text-h4 leading-[1.3] disabled:text-gray-700',
          // Grey 600 in both cases, not the lighter grey of the board: 4.5:1 on white (AA).
          'placeholder:text-gray-600',
          propose ? 'placeholder:font-semibold' : 'placeholder:font-normal',
        )}
      />
      {propose && titre === '' ? (
        <span id={idPropose} className="sr-only">
          Titre proposé : {titrePropose}, envoyé si ce champ reste vide
        </span>
      ) : null}
      <Button
        variant="primary"
        disabled={verrouille}
        aria-disabled={lectureAffichee}
        onClick={() => {
          // Not `disabled`: the button keeps the keyboard focus while the file is read.
          if (!lectureAffichee) refEntree.current?.click();
        }}
        icon="upload_file"
      >
        charger un fichier
      </Button>
      <input
        ref={refEntree}
        type="file"
        accept={ACCEPT_FICHIER}
        hidden
        tabIndex={-1}
        data-testid="entree-fichier"
        onChange={surChoix}
      />
    </div>
  );
}
